import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    CONFIRMATION_TYPES,
    DashboardButtonComponent,
    DashboardButtonModel,
    EscalationComponent,
    EscalationInstanceToken,
    EscalationInterface,
    EventService,
    loadSoftwareLicences,
    ModalClass,
    NOTIFICATION_STATUS_CODE,
    NotificationDetailState,
    NotificationModel,
    notificationRefreshRequired,
    NotificationStateUtilities,
    NotificationStatusInstanceToken,
    NotificationStatusInterface,
    NotificationUpdateService,
    selectAnyOtherEntryInStatusDisp,
    selectCentricarePCHEnabled,
    selectDisplayEtaAlert,
    selectEnableRemoteWork,
    selectEnableSafetyAssessmentForm,
    selectEscalateNotificationTypes,
    selectNotificationTypeLabel,
    selectSafetyAssessmentForm,
    selectShowRemoteStatusChangeWarning,
    stateToNotificationIdModel,
    StatusModel,
    statusStateToModel,
    SynchronizationService,
    TravelStatusUpdateModel,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { PaceStatusStepEntry } from '@evorait/pace-components';
import {
    BaseFormsListComponent,
    FormsConstants,
    FormsListComponentInstanceToken,
    getIsSAFormSubmitted,
    loadSafetyAssessmentForm,
} from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { firstValueFrom, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { AlertService } from '../../core/services/eta-alert/alert.service';
import { OdataTravelStatusUpdateService } from '../travel-status-update/odata-travel-status-update.service';
import { TravelStatusUpdateComponent } from '../travel-status-update/travel-status-update.component';
import { STATUS_STEP_ACTION } from '../status-step-button/status-step-action.enum';
import { softwareLicenceEquipmentKey } from '../../../../../../libs/pace-core/src/lib/state/software-licences/software-licences.utils';

/** The actions that may be taken even while another notification is still waiting to be started. */
const ACTIONS_ALLOWED_ALONGSIDE_DISPATCHED = new Set<string>([
    STATUS_STEP_ACTION.ETA,
    STATUS_STEP_ACTION.CRM,
    STATUS_STEP_ACTION.SCRM,
]);

/** The travel-status dialog handles these; everything else has its own branch. */
const TRAVEL_STATUS_ACTIONS = new Set<string>([
    STATUS_STEP_ACTION.ETA,
    STATUS_STEP_ACTION.ROSR,
    STATUS_STEP_ACTION.FCC,
    STATUS_STEP_ACTION.TRAV,
    STATUS_STEP_ACTION.OSR,
]);

/**
 * Runs the status changes the notification-details screen's step button offers.
 *
 * The current status, the availability checks and the "is another notification waiting" query all
 * come from the notification store; status writes go to the notification-*status* service, which owns
 * them.
 *
 * The dialogs and the escalation / ETA timers still take a `NotificationModel`, which the screen
 * supplies from the store through `NotificationModelFactory`.
 */
@Injectable({ providedIn: 'root' })
export class NotificationDetailsStatusStepService {
    private readonly log: LogInterface = inject(LogInstanceToken);
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly modalService = inject(BsModalService);
    private readonly clientManager = inject<ClientManagerInterface>(ClientManagerInstanceToken);
    private readonly notificationStatusService = inject<NotificationStatusInterface>(NotificationStatusInstanceToken);
    private readonly escalationService = inject<EscalationInterface>(EscalationInstanceToken);
    private readonly notificationUpdateService = inject(NotificationUpdateService);
    private readonly odataTravelStatusUpdateService = inject(OdataTravelStatusUpdateService);
    private readonly synchronizationService = inject(SynchronizationService);
    private readonly appNotificationService = inject(AppNotificationService);
    private readonly eventService = inject(EventService);
    private readonly alertService = inject(AlertService);
    private readonly formsListComponent = inject<BaseFormsListComponent>(FormsListComponentInstanceToken);

    private readonly enableSafetyAssessmentForm = this.store.selectSignal(selectEnableSafetyAssessmentForm);
    private readonly safetyAssessmentForm = this.store.selectSignal(selectSafetyAssessmentForm);
    private readonly showRemoteStatusChangeWarning = this.store.selectSignal(selectShowRemoteStatusChangeWarning);
    private readonly enableRemoteWork = this.store.selectSignal(selectEnableRemoteWork);
    private readonly displayEtaAlert = this.store.selectSignal(selectDisplayEtaAlert);
    private readonly centricarePCHEnabled = this.store.selectSignal(selectCentricarePCHEnabled);

    private readonly statusSubmittedSubject = new Subject<NOTIFICATION_STATUS_CODE>();
    /** The status a completed change moved the notification to — what re-labels the step button. */
    public readonly statusSubmitted$: Observable<NOTIFICATION_STATUS_CODE> = this.statusSubmittedSubject.asObservable();

    /**
     * Runs the action the user picked, after the two checks that can stop it: a status that another
     * device has moved on in the meantime, and a notification that may not be started while another
     * one is still waiting.
     *
     * @param action the picked action
     * @param state the notification's detail state
     * @param notification the store-built model the dialogs and timers take
     */
    public async runAction(
        action: PaceStatusStepEntry,
        state: NotificationDetailState,
        notification: NotificationModel,
    ): Promise<void> {
        const status = statusStateToModel(state.status);

        if (await this.isStatusOutdated(state, status)) {
            return;
        }
        if (await this.isBlockedByDispatchedNotification(action, state, status)) {
            return;
        }
        if (this.needsRemoteWorkConfirmation(action.id, state)) {
            this.confirmRemoteWorkChange(action.id, state, notification);
            return;
        }
        this.handleAction(action.id, state, notification);
    }

    /**
     * Records the ATS status once the Safety Assessment form has been submitted, so the step button
     * moves on without the user having to set it. Only relevant while ATS is actually available.
     *
     * @param state the notification's detail state
     * @returns an observable completing after the status was recorded, or immediately when it was not
     */
    public trackSafetyAssessmentSubmission(state: NotificationDetailState): Observable<unknown> {
        if (!this.enableSafetyAssessmentForm() || !state.availableStatus.includes(NOTIFICATION_STATUS_CODE.ATS)) {
            return of(undefined);
        }
        return this.store.select(getIsSAFormSubmitted(this.safetyAssessmentForm(), state.notificationId.qmNum)).pipe(
            take(1),
            distinctUntilChanged(),
            filter((isSubmitted) => isSubmitted),
            switchMap(() => {
                const now = moment();
                return this.saveStatus(state, new StatusModel(NOTIFICATION_STATUS_CODE.ATS, now, now));
            }),
            tap(() => this.statusSubmittedSubject.next(NOTIFICATION_STATUS_CODE.ATS)),
        );
    }

    /**
     * Records the confirmed status for a notification that was completed on another device, which is
     * what lets the screen show the "already completed" dialog rather than a stale action.
     *
     * @param state the notification's detail state
     */
    public saveConfirmedStatus(state: NotificationDetailState): Observable<boolean> {
        const now = moment();
        return this.saveStatus(state, new StatusModel(NOTIFICATION_STATUS_CODE.CRM, now, now));
    }

    private saveStatus(state: NotificationDetailState, status: StatusModel): Observable<boolean> {
        const identifier = stateToNotificationIdModel(state.notificationId);
        this.log.debug('NotificationDetailsStatusStepService: saveStatus', {
            notificationId: identifier.toString(),
            status: status.toString(),
        });
        return this.notificationStatusService.saveNotificationStatus(identifier, status);
    }

    /**
     * Whether another device has already moved the notification on. When it has, the user is told
     * and a refresh is requested instead of the action running against a stale status.
     *
     * @param state the notification's detail state
     * @param status the status the screen is showing
     */
    private async isStatusOutdated(state: NotificationDetailState, status: StatusModel): Promise<boolean> {
        const identifier = stateToNotificationIdModel(state.notificationId);
        const isOutdated = await firstValueFrom(
            forkJoin({
                recentlyCompletedNotifications: this.notificationStatusService.loadRecentlyCompletedNotifications(),
                notificationStatusList: this.notificationStatusService.syncNotificationStatusList(),
            }).pipe(
                take(1),
                map(({ recentlyCompletedNotifications }) => {
                    const newStatus = this.notificationStatusService.getLatestNotificationStatusForId(identifier);
                    // The legacy completed list is keyed by `NotificationIdentifierModel.toString()`.
                    const wasRecentlyCompleted =
                        !!recentlyCompletedNotifications.completedNotifications[identifier.toString()];
                    return (
                        (newStatus !== undefined && status.statusCode !== newStatus.statusCode) || wasRecentlyCompleted
                    );
                }),
            ),
        );

        if (isOutdated) {
            this.modalService.show(YesNoModalDialogComponent, {
                backdrop: 'static',
                class: ModalClass.MODAL_MEDIUM,
                initialState: {
                    model: new YesNoModalDialogModel(
                        <string>this.translate.instant('NOTIFICATION_STATUS_UPDATED_TITLE'),
                        <string>this.translate.instant('NOTIFICATION_STATUS_UPDATED_BODY'),
                        <string>this.translate.instant('NOTIFICATION_STATUS_UPDATED_ACTION'),
                    ),
                },
                ariaLabelledBy: 'yes-no-modal-title',
            });
            this.store.dispatch(notificationRefreshRequired());
        }
        return isOutdated;
    }

    /**
     * Whether starting this notification has to wait: another one is still dispatched, and the
     * picked action would begin work on this one.
     *
     * @param action the picked action
     * @param state the notification's detail state
     * @param status the status the screen is showing
     */
    private async isBlockedByDispatchedNotification(
        action: PaceStatusStepEntry,
        state: NotificationDetailState,
        status: StatusModel,
    ): Promise<boolean> {
        if (
            status.statusCode === NOTIFICATION_STATUS_CODE.OSR ||
            status.isToBeSigned() ||
            ACTIONS_ALLOWED_ALONGSIDE_DISPATCHED.has(action.id)
        ) {
            return false;
        }
        const isAnyOtherDispatched = await firstValueFrom(
            this.store.select(selectAnyOtherEntryInStatusDisp(state.notificationId)).pipe(take(1)),
        );
        if (!isAnyOtherDispatched) {
            return false;
        }
        this.modalService.show(DashboardButtonComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new DashboardButtonModel(
                    <string>this.translate.instant('DASHBOARD_NOTIFICATION_NEW_NOTIFICATION_WARNING_TITLE', {
                        status: action.label,
                    }),
                    <string>this.translate.instant('DASHBOARD_NOTIFICATION_NEW_NOTIFICATION_WARNING_BODY'),
                ),
            },
            ariaLabelledBy: 'dashboard-button-modal-title',
        });
        return true;
    }

    /**
     * Whether the user should be warned before mixing remote and on-site work: a notification
     * planned as remote support being started on site, or one planned on site being started
     * remotely.
     *
     * @param actionId the picked action
     * @param state the notification's detail state
     */
    private needsRemoteWorkConfirmation(actionId: string, state: NotificationDetailState): boolean {
        if (!this.showRemoteStatusChangeWarning() || !this.enableRemoteWork()) {
            return false;
        }
        const isRemoteSupport = state.userStatus.includes(ConfigConstants.USER_STATUS.REMT);
        const startsOnSite = actionId === STATUS_STEP_ACTION.OSR || actionId === STATUS_STEP_ACTION.TRAV;
        const startsRemotely = actionId === STATUS_STEP_ACTION.FCC || actionId === STATUS_STEP_ACTION.ROSR;

        return (
            (isRemoteSupport && state.status.statusCode !== NOTIFICATION_STATUS_CODE.TRAV && startsOnSite) ||
            (!isRemoteSupport && startsRemotely)
        );
    }

    private confirmRemoteWorkChange(
        actionId: string,
        state: NotificationDetailState,
        notification: NotificationModel,
    ): void {
        this.log.info(`NotificationDetailsStatusStepService: confirming status change to ${actionId}`);
        const onSiteLabel = <string>this.translate.instant('NOTIFICATION_STATUS_ONSITE_LABEL');
        const remoteLabel = <string>this.translate.instant('NOTIFICATION_STATUS_REMOTE_LABEL');
        const selectedStatus =
            actionId === STATUS_STEP_ACTION.TRAV || actionId === STATUS_STEP_ACTION.OSR ? onSiteLabel : remoteLabel;
        const notificationMarkedAs = state.userStatus.includes(ConfigConstants.USER_STATUS.REMT)
            ? remoteLabel
            : onSiteLabel;

        const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_REMOTE_WORK_WARNING_LABEL'),
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_REMOTE_WORK_WARNING_BODY', {
                        planned: notificationMarkedAs,
                        startAs: selectedStatus,
                    }),
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_REMOTE_WOR_WARNING_YES_BUTTON'),
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_REMOTE_WOR_WARNING_CANCEL_BUTTON'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });
        modalInstance.content?.modalResult.pipe(take(1)).subscribe((isConfirmed: boolean) => {
            if (isConfirmed) {
                this.handleAction(actionId, state, notification);
            }
        });
    }

    private handleAction(actionId: string, state: NotificationDetailState, notification: NotificationModel): void {
        // The software licences are loaded up front so they are available offline. Agreed with the
        // customer to load them when work starts — see https://evorait.atlassian.net/browse/SHP-5638.
        const loadsLicences =
            this.clientManager.isNgmP58() &&
            (actionId === STATUS_STEP_ACTION.OSR || actionId === STATUS_STEP_ACTION.SOSR);
        if (loadsLicences) {
            this.loadSoftwareLicences(notification);
        }

        if (TRAVEL_STATUS_ACTIONS.has(actionId)) {
            this.openTravelStatusUpdate(actionId, state, notification);
            return;
        }

        switch (actionId) {
            case STATUS_STEP_ACTION.ATS:
                this.openSafetyAssessmentForm(state, notification);
                break;
            // SOSR is the special OSR of PACE US: the user cannot change any date or time, so the
            // travel-status dialog is skipped in favour of a plain confirmation.
            case STATUS_STEP_ACTION.SOSR:
                this.confirmSpecialOnSite(state, notification);
                break;
            case STATUS_STEP_ACTION.CRM:
                this.openConfirmation(state, CONFIRMATION_TYPES.NORMAL);
                break;
            case STATUS_STEP_ACTION.SCRM:
                // A notification that got cancelled while its details screen was open cannot be
                // short-confirmed, so it is rerouted to the full confirmation instead.
                this.openConfirmation(
                    state,
                    this.isCancelled(state) ? CONFIRMATION_TYPES.NORMAL : CONFIRMATION_TYPES.SHORT,
                );
                break;
            case STATUS_STEP_ACTION.ESCALATE:
                this.openEscalation(state, notification);
                break;
            case STATUS_STEP_ACTION.FORMS:
                this.openPaceForms(state);
                break;
            default:
                this.log.warn(`NotificationDetailsStatusStepService: cannot handle status step action ${actionId}`);
        }
    }

    private isCancelled(state: NotificationDetailState): boolean {
        return (
            statusStateToModel(state.status).isCancelled() ||
            state.isCancelled.length > 0 ||
            this.notificationUpdateService.checkIfCancellationExists(stateToNotificationIdModel(state.notificationId))
        );
    }

    private openConfirmation(state: NotificationDetailState, type: CONFIRMATION_TYPES): void {
        this.router
            .navigate([
                ConfigConstants.routeUrlConfirmation,
                state.notificationId.frontId,
                state.notificationId.qmNum,
                state.notificationId.maNum,
                type,
            ])
            .catch((error: Error) =>
                this.log.error('NotificationDetailsStatusStepService: openConfirmation: router.navigate: ', error),
            );
    }

    /**
     * The dialog that records a status change together with its start and end time. What follows the
     * change depends on the new status: on-site starts the escalation timer and the PCH reminder, an
     * announced arrival starts the ETA alert, and anything else clears it.
     *
     * @param actionId the picked action
     * @param state the notification's detail state
     * @param notification the store-built model the dialog and the timers take
     */
    private openTravelStatusUpdate(
        actionId: string,
        state: NotificationDetailState,
        notification: NotificationModel,
    ): void {
        const modalInstance = this.modalService.show(TravelStatusUpdateComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new TravelStatusUpdateModel(notification, <NOTIFICATION_STATUS_CODE>actionId),
            },
            ariaLabelledBy: 'travel-status-update-modal-title',
        });

        modalInstance.content?.statusSubmitted.pipe(take(1)).subscribe((newStatus: NOTIFICATION_STATUS_CODE) => {
            this.statusSubmittedSubject.next(newStatus);
            const displayEtaAlert = this.displayEtaAlert();

            if (newStatus === NOTIFICATION_STATUS_CODE.OSR) {
                this.escalationService.setTimerForEscalation(notification, moment.duration(0));
                if (this.centricarePCHEnabled()) {
                    this.eventService.triggerPCHAlertOnOSR();
                }
            } else if (displayEtaAlert && newStatus === NOTIFICATION_STATUS_CODE.ETA) {
                this.alertService.setTimerForEtaAlert(notification);
            } else if (displayEtaAlert) {
                this.alertService.removeEtaTimer(notification);
            }
        });
    }

    /**
     * PACE US' special on-site status: a plain yes/no, then the status is sent, recorded and pushed
     * out with the rest of the offline data.
     *
     * @param state the notification's detail state
     * @param notification the store-built model the travel-status model takes
     */
    private confirmSpecialOnSite(state: NotificationDetailState, notification: NotificationModel): void {
        const yesNoModal = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_SMALL,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('ACCEPT_NOTIFICATION_TITLE'),
                    <string>this.translate.instant('ACCEPT_NOTIFICATION_BODY'),
                    <string>this.translate.instant('MODAL_OK'),
                    <string>this.translate.instant('MODAL_CANCEL'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });

        const travelStatusUpdate = new TravelStatusUpdateModel(notification, NOTIFICATION_STATUS_CODE.SOSR);

        yesNoModal.content?.modalResult
            .pipe(
                take(1),
                filter((isConfirmed: boolean) => isConfirmed),
                mergeMap(() => this.odataTravelStatusUpdateService.saveStatus(travelStatusUpdate)),
                mergeMap(() => {
                    this.log.debug(
                        `NotificationDetailsStatusStepService: status updated successfully to ${travelStatusUpdate.nextStatus}`,
                    );
                    return this.saveStatus(
                        state,
                        new StatusModel(
                            travelStatusUpdate.nextStatus,
                            travelStatusUpdate.date.value,
                            travelStatusUpdate.endDate.value,
                        ),
                    );
                }),
                mergeMap(() => {
                    this.statusSubmittedSubject.next(travelStatusUpdate.nextStatus);
                    return this.synchronizationService.sendOfflineData();
                }),
                catchError((error) => {
                    this.log.error(
                        `NotificationDetailsStatusStepService: error in sendOfflineData after submitting the special on-site status: ${JSON.stringify(error)}`,
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.WARN,
                        <string>this.translate.instant('APP_SYNC_FAILED_TITLE'),
                        <string>this.translate.instant('APP_SYNC_FAILED_SYNCHRONIZATION'),
                    );
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    /**
     * The escalation dialog. Escalating stops the escalation and ETA timers, since what they were
     * counting down to has now happened.
     *
     * @param state the notification's detail state
     * @param notification the store-built model the dialog and the timers take
     */
    private openEscalation(state: NotificationDetailState, notification: NotificationModel): void {
        if (
            this.clientManager.isNgmP58() &&
            !this.store.selectSignal(selectEscalateNotificationTypes(state.notificationType))()
        ) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('NOTIFICATION_DETAILS_CONFIRM_TASK_ESCALATION_TITLE'),
                <string>this.translate.instant('NOTIFICATION_DETAILS_CONFIRM_TASK_ESCALATION_NOT_SUPPORTED', {
                    notificationType: this.store.selectSignal(selectNotificationTypeLabel(state.notificationType))(),
                }),
            );
            return;
        }
        const modalInstance = this.modalService.show(EscalationComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: { notification },
            ariaLabelledBy: 'escalation-modal-title',
        });
        modalInstance.content?.modalResult.pipe(take(1)).subscribe((isEscalated) => {
            if (!isEscalated) {
                return;
            }
            this.escalationService.removeEscalationTimer(notification);
            if (this.displayEtaAlert()) {
                this.alertService.removeEtaTimer(notification);
            }
        });
    }

    private openPaceForms(state: NotificationDetailState): void {
        const formList = this.formsListComponent as unknown as new (...args: unknown[]) => BaseFormsListComponent;

        this.modalService.show(formList, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                notificationIdentifier: {
                    qmNum: state.notificationId.qmNum,
                    maNum: state.notificationId.maNum,
                    frontId: state.notificationId.frontId,
                },
                useFormListCache: false,
            } as BaseFormsListComponent,
            ariaLabelledBy: 'forms-list-modal-title',
        });
    }

    private openSafetyAssessmentForm(state: NotificationDetailState, notification: NotificationModel): void {
        const mainEquipment = notification.getMainEquipment();
        if (!mainEquipment) {
            throw new Error(
                `NotificationDetailsStatusStepService: openSafetyAssessmentForm: no main equipment for notification ${NotificationStateUtilities.notificationIdToString(state.notificationId)}`,
            );
        }
        this.store.dispatch(
            loadSafetyAssessmentForm({
                notification: state.notificationId,
                form: {
                    formId: this.safetyAssessmentForm(),
                    equipmentId: mainEquipment.equnr.value,
                    materialId: mainEquipment.matnr.value,
                },
                status: FormsConstants.FORMSTATUS.LOCK,
            }),
        );
    }

    private loadSoftwareLicences(notification: NotificationModel): void {
        const mainEquipment = notification.getMainEquipment();
        if (!mainEquipment) {
            throw new Error('NotificationDetailsStatusStepService: loadSoftwareLicences: no main equipment available!');
        }

        const equipmentKey = softwareLicenceEquipmentKey(mainEquipment);
        this.store.dispatch(
            loadSoftwareLicences({ equipmentKey, matnr: mainEquipment.matnr.value, sernr: mainEquipment.sernr.value }),
        );
    }
}
